import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Search } from "lucide-react";
import { MaskLines } from "../components/Motion";
import { OpenBadge } from "../components/OpenBadge";

const EASE = [0.16, 1, 0.3, 1];

const HeroFrame = ({ mx, my }) => {
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 70, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 70, damping: 18 });
  const sx = useTransform(mx, (v) => `${v * 100}%`);
  const sy = useTransform(my, (v) => `${v * 100}%`);
  const spot = useMotionTemplate`radial-gradient(420px circle at ${sx} ${sy}, rgba(255,85,0,0.28), transparent 60%)`;
  return (
    <div style={{ perspective: 1200 }} className="relative">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
          className="clip-notch relative aspect-[4/5] w-full overflow-hidden bg-slab md:aspect-[4/5]"
        >
          <img src="/img/board.jpg" alt="Open laptop logic board on the Wilko Tech bench" className="h-full w-full scale-110 object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
          <motion.div style={{ background: spot }} className="absolute inset-0 mix-blend-screen" />
          <motion.div
            initial={{ top: "0%", opacity: 1 }}
            animate={{ top: "100%", opacity: 0 }}
            transition={{ duration: 1.6, ease: "easeInOut", delay: 0.6 }}
            className="absolute inset-x-0 h-px bg-ember shadow-[0_0_24px_4px_rgba(255,85,0,0.7)]"
          />
        </motion.div>
        <div style={{ transform: "translateZ(60px)" }} className="absolute -bottom-5 -left-4 border border-white/10 bg-ink/90 px-4 py-3 backdrop-blur-xl md:-left-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Bench ticket</p>
          <p className="font-mono text-lg text-ember">WT-·····</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain grid-lines relative overflow-hidden pt-28 md:pt-32">
      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-20 md:px-10 lg:grid-cols-12 lg:gap-8 lg:pb-28">
        <motion.div style={{ y: yText }} className="relative z-10 lg:col-span-7 lg:pt-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="mb-8 flex flex-wrap items-center gap-3">
            <OpenBadge />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Laptop · Desktop · Repair</span>
          </motion.div>
          <h1 className="sr-only">Wilko Tech LTD laptop and desktop repair</h1>
          <MaskLines
            onLoad
            testId="hero-headline"
            className="font-display font-black uppercase leading-[0.86] tracking-[-0.03em] text-[clamp(3.4rem,10vw,9.5rem)]"
            lines={[
              <span key="a">Dead</span>,
              <span key="b">machines,</span>,
              <span key="c" className="text-outline">brought</span>,
              <span key="d">back<span className="text-ember">.</span></span>,
            ]}
          />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="mt-10 max-w-xl"
          >
            <p className="text-base leading-relaxed text-slate-300 md:text-lg">
              Wilko Tech LTD repairs laptops and desktops. Tell us what's wrong in two minutes, have it picked up or post it to us, and follow the fix with your own reference number.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/request" data-testid="hero-request-repair-btn" className="group inline-flex items-center justify-center gap-3 bg-ember px-7 py-4 font-semibold text-ink transition-[background-color,transform] duration-300 hover:bg-amber-400 active:scale-[0.97]">
                Request a repair
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/track" data-testid="hero-track-repair-btn" className="group inline-flex items-center justify-center gap-3 border border-white/15 px-7 py-4 font-semibold transition-[border-color,background-color] duration-300 hover:border-ember hover:bg-white/[0.03]">
                <Search className="h-4 w-4" /> Track a repair
              </Link>
            </div>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <HeroFrame mx={mx} my={my} />
        </motion.div>
      </div>
    </section>
  );
};
